import { handleApiError } from '@api';
import config from '@config';
import { HTTPClient } from '@platform2-lib/httpz';
import * as schema from '@schema';
import type {
  Chat,
  ChatListResponse,
  DatasetTree,
  GenerateChatTitleResponse
} from 'types/schema';

const client = new HTTPClient(
  config.env,
  `${config.api.origin}/spark-agent-v2`
);

const { maxSize } = config.chat.sessions;

export const pageGenerator = (function* () {
  let step = maxSize;

  while (true) {
    step += maxSize;
    yield step;
  }
})();

async function getAll(
  page: number = maxSize
): Promise<ChatListResponse['chat_groups']> {
  try {
    const { ['chat_groups']: chatList } = await client.get(
      `/chats?skip=${page - maxSize}&take=${maxSize}&order=desc&agent=${config.chat.sseAgent}`,
      schema.ChatListResponse
    );

    return chatList;
  } catch (error) {
    handleApiError(error, 'Unable to retrieve recent chats.');

    return [];
  }
}

async function getById(chatId: Chat['id']): Promise<Chat> {
  try {
    const response = await client.get(
      `/chat/${chatId}?agent=${config.chat.sseAgent}`,
      schema.Chat
    );

    return response;
  } catch (error) {
    handleApiError(error, `Unable to retrieve chat '${chatId}'.`, true);
  }
}

async function updateTitleById(
  chatId: Chat['id'],
  title: Chat['title']
): Promise<unknown> {
  try {
    const response = await client.put(
      `/chat/${chatId}/title/edit`,
      { title },
      schema.EditChatTitleResponse
    );

    return response;
  } catch (error) {
    handleApiError(error, `Unable to update the title of chat '${chatId}'.`);
  }
}

async function getGeneratedTitleById(
  chatId: string
): Promise<GenerateChatTitleResponse> {
  try {
    const response = await client.put(
      `/chat/${chatId}/title/generate`,
      null,
      schema.GenerateChatTitleResponse
    );

    return response;
  } catch (error) {
    handleApiError(
      error,
      `Unable to retrieve generated title for chat '${chatId}'.`,
      true
    );
  }
}

async function deleteById(chatId: Chat['id']): Promise<boolean> {
  try {
    const response = await client.delete(`/chat/${chatId}`);

    return response;
  } catch (error) {
    handleApiError(error, `Unable to delete chat '${chatId}'.`);
  }
}

async function getDatasetTree(chatId: Chat['id']): Promise<DatasetTree> {
  try {
    const url = chatId ? `/chat/${chatId}/datasets/tree` : '/datasets/tree';
    const response = await client.get(url, schema.DatasetTree);

    return response;
  } catch (error: unknown) {
    handleApiError(error, 'Failed to retrieve the dataset tree.');

    return { children: [] };
  }
}

export const chats = {
  pageGenerator,
  deleteById,
  getAll,
  getById,
  getDatasetTree,
  getGeneratedTitleById,
  updateTitleById
};
